import { useSession } from "@clerk/clerk-react";
import { isAxiosError, type AxiosResponse } from "axios";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIframePoolStore } from "@/components/Canvas/IframePool/iframePoolStore";
import { useDatabaseSyncWrapperStore } from "@/components/contexts/databaseSyncStore";
import useLocalMode from "@/hooks/useLocalMode";
import { fetchGitStatus } from "@/store/gitStatusSlice";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import {
  type DevErrorJson,
  type RunningServerCommand,
  ServerCommandType,
  useDevServerStore,
} from "@/store/zustand/devServerStore";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { track } from "@/utils/events/tracker";
import { axiosGet, axiosPost } from "./axiosWrapper";
import { stripAnsiFromTerminalOutput } from "./console";
import { STORYBOARD_TYPES } from "./constants";
import {
  getAxiosErorrMessage,
  getUnexpiredToken,
  useActiveCanvas,
  useActiveMainCanvas,
  useAutoUpdateRef,
  useCanvasId,
} from "./utils";
import {
  usePagesStore,
  updateFromPersistedPages,
} from "@/store/zustand/pagesStore";
import { toast } from "sonner";

export const startDevServerAndReportErrors = async ({
  addTerminalErrors,
  canvasId,
  extraEnvVariables,
  abortController,
  token,
  restart,
  traceId,
}: {
  addTerminalErrors?: (errors: DevErrorJson[]) => void;
  canvasId: string;
  extraEnvVariables?: {
    [key: string]: string;
  };
  abortController?: AbortController;
  token: string;
  restart?: boolean;
  traceId?: string;
}): Promise<{
  success: boolean;
  error?: Error;
  errorConsoleOutput?: string;
}> => {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };

  if (traceId) {
    headers["x-trace-id"] = traceId;
  }

  const response = await axiosPost(
    `${
      import.meta.env.VITE_BACKEND_BASE_URL
    }/canvases/${canvasId}/devServer/startDevServer`,
    {
      restart: restart,
      extraEnvVariables,
    },
    {
      headers,
      signal: abortController?.signal,
      errorMessage: "Failed to start dev server",
      showToast: true,
      throwOnError: false,
    },
  );

  if (
    response?.data?.error &&
    response?.data?.errorConsoleOutput &&
    addTerminalErrors
  ) {
    addTerminalErrors([
      {
        message: stripAnsiFromTerminalOutput(response.data.errorConsoleOutput),
        filename: "Restarting dev server",
        stack: "",
        count: 1,
      },
    ]);
  }

  return response?.data ?? { error: "Unknown error" };
};

export interface StartServerInterface {
  extraEnvVariables?: {
    [key: string]: string;
  };
  abortController?: AbortController;
  forceRestart?: boolean;

  // If we know the container is running for sure, don't check the status to relaunch
  doNotCheckContainer?: boolean;
  doNotReloadCodebase?: boolean;
  traceId?: string;
}

const addRunningServerCommand = (command: RunningServerCommand) => {
  // If there are other running server commands, abort them
  useDevServerStore.getState().runningServerCommands.forEach((cmd) => {
    cmd.abortController?.abort();
  });

  // If this running server command doesn't have an abort controller, create one
  if (!command.abortController) {
    command.abortController = new AbortController();
  }

  useDevServerStore.getState().setRunningServerCommands([command]);
};

const removeRunningServerCommand = (command: RunningServerCommand) => {
  useDevServerStore
    .getState()
    .setRunningServerCommands(
      useDevServerStore
        .getState()
        .runningServerCommands.filter((cmd) => cmd !== command),
    );
};

export const useStartServer = () => {
  const canvasId = useCanvasId();

  const mainCanvas = useActiveMainCanvas();

  const { session } = useSession();

  const triggerRefetchProjectLoadingProgress = useDatabaseSyncWrapperStore(
    (state) => state.triggerRefetchProjectLoadingProgress,
  );

  const launchContainer = useLaunchContainer();

  const loadCodebase = useLoadCodebaseInternal();

  const devServerStatus = useSelector(
    (state: any) => state.codebase.devServerStatus,
  );

  const devServerStatusRef = useRef(devServerStatus);
  useEffect(() => {
    devServerStatusRef.current = devServerStatus;
  }, [devServerStatus]);

  const addTerminalErrors = useDevServerStore(
    (state) => state.addTerminalErrors,
  );

  return async ({
    extraEnvVariables,
    abortController,
    forceRestart,
    doNotCheckContainer,
    doNotReloadCodebase,
    traceId,
  }: StartServerInterface = {}): Promise<{
    success: boolean;
    error?: string;
  }> => {
    const supabaseClient = useSupabaseStore.getState().supabaseClient;
    if (!supabaseClient) {
      return {
        success: false,
        error: "No supabase client",
      };
    }

    const containerRunning =
      devServerStatusRef.current?.containerStatus?.running;

    // Gets set back on the backend when done
    const relaunchingContainer = !doNotCheckContainer && !containerRunning;
    const updateDict = {
      started_dev_server: false,
      dev_server_responding: false,
      devtools_script_responsive: false,
      node_modules_installed: false,
      last_updated: new Date().toISOString(),
    };

    useCodebaseStore.getState().setProjectLaunchRunning(true);

    if (!doNotReloadCodebase) {
      updateDict["files_loaded"] = false;
    }

    if (relaunchingContainer) {
      updateDict["launched_container"] = false;
      updateDict["stopped_container"] = false;
    }

    await supabaseClient
      .from("project_loading_progress")
      .update(updateDict)
      .eq("canvas_id", canvasId);

    triggerRefetchProjectLoadingProgress?.();

    if (abortController?.signal.aborted) {
      useCodebaseStore.getState().setProjectLaunchRunning(false);
      useCodebaseStore.getState().setProjectLaunchHadError(true);
      return {
        success: false,
        error: "Operation aborted by user",
      };
    }

    // If container isn't running, launch it
    if (!containerRunning && !doNotCheckContainer) {
      const { success, error } = await launchContainer({
        abortController,
        forceRelaunch: true,
        traceId,
      });

      if (!success) {
        useCodebaseStore.getState().setProjectLaunchRunning(false);
        useCodebaseStore.getState().setProjectLaunchHadError(true);
        return {
          success: false,
          error: error || "Error launching container",
        };
      }
    }

    if (!doNotReloadCodebase) {
      await loadCodebase({
        abortController,
        traceId,
      });
    }

    const command = {
      commandType: ServerCommandType.START_SERVER,
      startTime: new Date(),
      abortController: abortController,
    };

    addRunningServerCommand(command);

    if (!mainCanvas) {
      useCodebaseStore.getState().setProjectLaunchRunning(false);
      useCodebaseStore.getState().setProjectLaunchHadError(true);
      return {
        success: false,
        error: "Canvas not found",
      };
    }

    const startTime = performance.now();

    track("RESTART");
    useIframePoolStore.getState().refreshAllStoryboards();

    const token = await getUnexpiredToken(session);
    useCodebaseStore.getState().setProjectLaunchHadError(false);

    const { error } = await startDevServerAndReportErrors({
      canvasId,
      extraEnvVariables,
      token: token!,
      addTerminalErrors,
      abortController: command.abortController,
      restart: forceRestart,
      traceId,
    });

    removeRunningServerCommand(command);

    triggerRefetchProjectLoadingProgress?.();

    // Set the project launch status to success even if there is an error to close the modal
    const endTime = performance.now();

    if (error) {
      if (!command.abortController?.signal.aborted) {
        useCodebaseStore.getState().setProjectLaunchHadError(true);
      }

      useCodebaseStore.getState().setProjectLaunchRunning(false);

      return {
        success: false,
        error: error?.message || "Unknown error",
      };
    }

    console.debug(`Restart request: ${endTime - startTime} ms`);

    useCodebaseStore.getState().setProjectLaunchRunning(false);
    useIframePoolStore.getState().refreshAllStoryboards();

    return {
      success: true,
    };
  };
};

export interface RelaunchContainerInterface {
  abortController?: AbortController;
  forceRelaunch?: boolean;
  traceId?: string;
}

export const useLaunchContainer = () => {
  const canvasId = useCanvasId();

  const { session } = useSession();

  return useCallback(
    async ({
      abortController,
      forceRelaunch,
      traceId,
    }: RelaunchContainerInterface = {}): Promise<{
      success: boolean;
      error?: string;
    }> => {
      const command = {
        commandType: ServerCommandType.START_SERVER,
        startTime: new Date(),
        abortController: abortController,
      };

      addRunningServerCommand(command);

      const startTime = performance.now();

      const token = await getUnexpiredToken(session);
      useCodebaseStore.getState().setProjectLaunchHadError(false);

      const supabaseClient = useSupabaseStore.getState().supabaseClient;
      if (!supabaseClient) {
        return {
          success: false,
          error: "No supabase client",
        };
      }

      // Gets set back on the backend when done
      await supabaseClient
        .from("project_loading_progress")
        .update({
          started_dev_server: false,
          dev_server_responding: false,
          devtools_script_responsive: false,
          node_modules_installed: false,
          launched_container: false,
          stopped_container: !forceRelaunch,
          last_updated: new Date().toISOString(),
        })
        .eq("canvas_id", canvasId);

      useDatabaseSyncWrapperStore
        .getState()
        .triggerRefetchProjectLoadingProgress();

      // Launch the container
      let error;
      try {
        const headers: Record<string, string> = {
          Authorization: `Bearer ${token}`,
        };

        if (traceId) {
          headers["x-trace-id"] = traceId;
        }

        await axiosPost(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/devServer/runContainer`,
          {
            relaunch: Boolean(forceRelaunch),
          },
          {
            headers,
            signal: command.abortController?.signal,
            errorMessage: "Failed to run container",
            showToast: true,
            throwOnError: false,
          },
        );
      } catch (e) {
        error = e;
      }

      removeRunningServerCommand(command);

      useDatabaseSyncWrapperStore
        .getState()
        .triggerRefetchProjectLoadingProgress();

      if (error) {
        if (!command.abortController?.signal.aborted) {
          useCodebaseStore.getState().setProjectLaunchHadError(true);
        }

        return {
          success: false,
          error: error?.message || "Unknown error",
        };
      }

      console.debug(`Relaunch request: ${performance.now() - startTime} ms`);

      return {
        success: true,
      };
    },
    [canvasId, session],
  );
};

export interface LoadCodebaseInterface {
  showProgress?: boolean;
  abortController?: AbortController;
  filesToLoad?: string[];
  traceId?: string;
  isRetryAfterRelaunch?: boolean;
}

const useLoadCodebaseInternal = (
  startServer?: (
    params: StartServerInterface,
  ) => Promise<{ success: boolean; error?: string }>,
) => {
  const canvasId = useCanvasId();
  const { isLocalMode } = useLocalMode();

  const { session } = useSession();

  const dispatch = useDispatch<any>();

  const setAllLookups = useCodebaseStore((state) => state.setAllLookups);
  const updateLookupsBasedOnPartialResponse = useCodebaseStore(
    (state) => state.updateLookupsBasedOnPartialResponse,
  );

  const triggerRefetchProjectLoadingProgress = useDatabaseSyncWrapperStore(
    (state) => state.triggerRefetchProjectLoadingProgress,
  );

  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

  const mainCanvas = useActiveMainCanvas();
  const mainCanvasRef = useAutoUpdateRef(mainCanvas);

  const launchContainer = useLaunchContainer();

  const loadCodebaseFn = async ({
    abortController,
    filesToLoad,
    traceId,
    isRetryAfterRelaunch = false,
  }: LoadCodebaseInterface = {}): Promise<any> => {
    if (!supabaseClient || !session) {
      return;
    }

    const supabaseJWT = await getUnexpiredToken(session);

    let loadResponse: AxiosResponse<any, any> | null = null;
    try {
      const headers: Record<string, string> = {
        Authorization: `Bearer ${supabaseJWT}`,
      };

      if (traceId) {
        headers["x-trace-id"] = traceId;
      }

      const legacyStoryboardFiles =
        mainCanvasRef.current?.storyboards
          ?.filter((sb) => sb.saved && sb.type === STORYBOARD_TYPES.COMPONENT)
          .map((sb) => sb.fullFilePath || "") || [];

      loadResponse = await axiosPost(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/canvases/${canvasId}/load`,
        {
          branch: null,
          isLocalMode,
          filesToLoad,
          legacyStoryboardFiles,
          enablePages: true,
        },
        {
          headers,
          signal: abortController?.signal,
          errorMessage: "Failed to load project",
          showToast: false,
          throwOnError: true,
        },
      );

      if (!loadResponse?.data) {
        throw new Error("Failed to load codebase info");
      }

      if (filesToLoad) {
        updateLookupsBasedOnPartialResponse(loadResponse?.data);
      } else {
        setAllLookups({
          lookups: loadResponse?.data,
          canvasId,
        });
      }

      dispatch(
        fetchGitStatus({
          session,
          canvasId,
        }),
      );

      // Update pages from loadAllFiles response
      if (loadResponse?.data?.pages) {
        updateFromPersistedPages(
          canvasId,
          loadResponse.data.pages,
          loadResponse.data.basePathToProject,
          loadResponse.data.tempobookPagesDir,
        );
      }
    } catch (e) {
      if (abortController?.signal.aborted) {
        return;
      } else if (isAxiosError(e)) {
        if (
          e.response?.data?.relaunchContainer &&
          !isRetryAfterRelaunch &&
          startServer
        ) {
          // Relaunch container and start dev server before retrying load
          const { success } = await launchContainer({
            abortController,
            forceRelaunch: true,
            traceId,
          });

          if (success) {
            // Retry the load operation, but mark it as a retry to prevent infinite recursion
            const res = await loadCodebaseFn({
              abortController,
              filesToLoad,
              traceId,
              isRetryAfterRelaunch: true,
            });

            // Start the dev server (without reloading codebase or checking container)
            if (res) {
              await startServer({
                abortController,
                traceId,
                doNotReloadCodebase: true,
                doNotCheckContainer: true,
              });
            }

            return res;
          }
        }

        toast.error(`Failed to load project: ${getAxiosErorrMessage(e)}`);
      }
    } finally {
    }

    triggerRefetchProjectLoadingProgress?.();

    return loadResponse?.data;
  };

  return loadCodebaseFn;
};

/**
 * This is a workaround to prevent the circular dependency between useLoadCodebase and useStartServer.
 */
export const useLoadCodebase = () => {
  const startServer = useStartServer();
  return useLoadCodebaseInternal(startServer);
};

export interface RunTerminalCommandInterface {
  command: string;
  abortController?: AbortController;
  extraEnvVariables?: {
    [key: string]: string;
  };
}

export const useRunTerminalCommand = () => {
  const canvasId = useCanvasId();
  const addTerminalErrors = useDevServerStore(
    (state) => state.addTerminalErrors,
  );

  const { session } = useSession();

  const devServerStatus = useSelector(
    (state: any) => state.codebase.devServerStatus,
  );

  const devServerStatusRef = useRef(devServerStatus);
  useEffect(() => {
    devServerStatusRef.current = devServerStatus;
  }, [devServerStatus]);

  const launchContainer = useLaunchContainer();
  const startServer = useStartServer();

  return async (
    inp: RunTerminalCommandInterface,
  ): Promise<{
    success: boolean;
    error?: string;
  }> => {
    const { command, abortController, extraEnvVariables } = inp || {};

    const supabaseJWT = await getUnexpiredToken(session);

    if (!devServerStatusRef.current?.containerStatus?.running) {
      // Launch the container
      const { success, error } = await launchContainer({
        abortController,
      });

      if (!success) {
        return {
          success: false,
          error: error || "Error relaunching container",
        };
      }

      // Also trigger starting the dev server
      await startServer({
        forceRestart: true,
        abortController,
      });
    }

    try {
      const res = await axiosPost(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/devServer/runTerminalCommand`,
        {
          command,
          extraEnvVariables,
        },
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
          signal: abortController?.signal,
          errorMessage: "Failed to run terminal command",
          showToast: true,
          throwOnError: false,
        },
      );

      if (res?.data?.error && res?.data?.errorConsoleOutput) {
        addTerminalErrors([
          {
            message: res.data.errorConsoleOutput,
            filename: "Terminal",
            stack: "",
            count: 1,
          },
        ]);
      }

      return {
        success: res?.data?.success || false,
        error: res?.data?.error,
      };
    } catch (error) {
      if (abortController?.signal.aborted) {
        return { success: false, error: "Operation aborted by user" };
      }

      return {
        success: false,
        error: error?.message || "Failed to run terminal command",
      };
    }
  };
};

export const useCheckDevServerHtmlErrors = () => {
  const canvasId = useCanvasId();
  const activeCanvas = useActiveCanvas();
  const { session } = useSession();

  const checkDevServerHtmlErrors = useCallback(
    async (url?: string) => {
      if (useIframePoolStore.getState().isCheckingDevServerHtmlErrors) {
        return;
      }

      useIframePoolStore.getState().setIsCheckingDevServerHtmlErrors(true);

      try {
        if (!canvasId) return false;

        const apiUrl = `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/checkDevserverHtmlErrors${
          url ? `?url=${encodeURIComponent(url)}` : ""
        }`;

        const supabaseJWT = await getUnexpiredToken(session);
        const response = await axiosGet(apiUrl, {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
          timeout: 10 * 1000,
          showToast: false,
        });

        if (!response?.data) {
          console.error("Could not fetch dev server HTML errors");
          return false;
        }

        const data = response.data as {
          success: boolean;
          statusCode: number;
          error?: {
            type: string;
            message: string;
            source: string;
            stack: string;
          };
        };

        if (data.error) {
          const error: DevErrorJson = {
            message: data.error.message,
            filename: data.error.source,
            stack: data.error.stack,
            count: 1,
          };

          // Add to all non-application storyboards that are currently visible
          const storyboardIds =
            activeCanvas?.storyboards
              ?.filter((sb) => !sb.deleted && !sb.renderStatus)
              .map((sb) => sb.id) || [];

          if (storyboardIds.length > 0) {
            storyboardIds.forEach((sbId) => {
              useDevServerStore.getState().addErrorForStoryboard(sbId, error);
            });
          } else {
            useDevServerStore.getState().addTerminalErrors([error]);
          }

          track("STORYBOARD_ERROR", {
            error: error,
            status_code: data.statusCode,
            error_type: data.error.type,
          });

          return true; // Error was found and handled
        }

        return false; // No error found
      } catch (error) {
        console.error("Failed to check dev server HTML errors", error);
        return false;
      } finally {
        useIframePoolStore.getState().setIsCheckingDevServerHtmlErrors(false);
      }
    },
    [canvasId, activeCanvas, session],
  );

  return checkDevServerHtmlErrors;
};

export const useProjectLaunchStatus = (): "loading" | "succeeded" | "error" => {
  const projectLaunchHadError = useCodebaseStore(
    (state) => state.projectLaunchHadError,
  );
  const projectLoadingProgress = useSelector(
    (state: any) => state.supabaseStore.projectLoadingProgress,
  );
  const projectLaunchRunning = useCodebaseStore(
    (state) => state.projectLaunchRunning,
  );
  const projectLaunchEverRan = useCodebaseStore(
    (state) => state.projectLaunchEverRan,
  );

  return useMemo(() => {
    if (projectLaunchHadError) {
      return "error";
    }

    // The last required step
    if (
      !projectLoadingProgress?.devtools_script_responsive ||
      projectLaunchRunning ||
      !projectLaunchEverRan
    ) {
      return "loading";
    }

    return "succeeded";
  }, [
    projectLaunchHadError,
    projectLoadingProgress,
    projectLaunchRunning,
    projectLaunchEverRan,
  ]);
};

export const useDevServerLaunched = () => {
  const projectLaunchEverRan = useCodebaseStore(
    (state) => state.projectLaunchEverRan,
  );
  const projectLaunchRunning = useCodebaseStore(
    (state) => state.projectLaunchRunning,
  );
  const projectLaunchHadError = useCodebaseStore(
    (state) => state.projectLaunchHadError,
  );
  return useMemo(() => {
    return (
      projectLaunchEverRan && !projectLaunchRunning && !projectLaunchHadError
    );
  }, [projectLaunchEverRan, projectLaunchRunning, projectLaunchHadError]);
};
